import * as React from 'react'
import * as classNames from 'classnames'
import styled from '@emotion/styled'
import { LeftMenuClass, Primary } from '@sphinx/components-styles'
import { MenuIcon } from '../icons'
import { Button, ButtonType } from '../button/button'

const MenuContext = React.createContext(false)

interface LeftMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean
  menuTabIndex?: number
}

export const LeftMenu: React.FC<LeftMenuProps> = ({
  children,
  open = false,
  menuTabIndex = -1,
  ...props
}) => {
  const [isOpen, setOpen] = React.useState(open)
  const toggleMenu = React.useCallback(
    () => setOpen(!isOpen),
    [isOpen, setOpen]
  )

  return (
    <MenuContext.Provider value={isOpen}>
      <LeftMenuContainer {...props} open={isOpen}>
        <ToggleMenu open={isOpen}>
          <Button
            buttonType={ButtonType.GHOST}
            onClick={toggleMenu}
            tabIndex={menuTabIndex}
          >
            <MenuIcon />
          </Button>
        </ToggleMenu>
        {children}
      </LeftMenuContainer>
    </MenuContext.Provider>
  )
}

interface ToggleContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  showOnOpen?: boolean
}

export const ToggleContainer: React.FC<ToggleContainerProps> = ({
  className,
  showOnOpen = false,
  ...props
}) => {
  const isOpen = React.useContext(MenuContext)
  const show = showOnOpen ? isOpen : !isOpen

  return (
    show && (
      <nav
        className={classNames(LeftMenuClass.navigation, className)}
        {...props}
      />
    )
  )
}

interface LeftMenuActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  column?: boolean
}

export const LeftMenuActions: React.FC<LeftMenuActionsProps> = ({
  children,
  column,
  className,
  ...props
}) => {
  const getClassName = (isColumn: boolean) =>
    isColumn ? LeftMenuClass.actionsColumn : LeftMenuClass.actionsRow

  return (
    <div className={classNames(getClassName(column), className)} {...props}>
      {children}
    </div>
  )
}

interface LeftMenuContainerProps extends React.HTMLAttributes<HTMLElement> {
  open?: boolean
}

const LeftMenuContainer: React.FC<LeftMenuContainerProps> = ({
  open,
  className,
  ...props
}) => {
  const componentClassName = classNames(
    open ? LeftMenuClass.containerOpen : LeftMenuClass.container,
    className
  )

  return <aside className={componentClassName} {...props} />
}

interface ToggleMenuProps extends React.HTMLAttributes<HTMLElement> {
  open?: boolean
}

export const ToggleMenu: React.FC<ToggleMenuProps> = ({
  open,
  className,
  ...props
}) => {
  const componentClassName = classNames(
    LeftMenuClass.toggleMenu,
    { [LeftMenuClass.toggleMenuOpen]: open },
    className
  )

  return <aside className={componentClassName} {...props} />
}

export const LeftMenuLink = styled.a<{ active?: boolean }>`
  min-width: 32px;
  height: 32px;
  box-sizing: border-box;

  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;

  color: ${({ active }) => (active ? Primary.orange[100] : Primary.navy[100])};

  & > svg > path {
    fill: ${({ active }) => (active ? Primary.orange[100] : Primary.navy[100])};
  }
`
